import {Injectable} from '@angular/core';
import {isTransactionType, Transaction} from '~local/app/types/Transaction';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {CommissionService, PaymentGroupFees} from './CommissionService';
import {CostOfProductService} from './CostOfProductService';
import {DataSourceResponse} from './DataSourceService';
import {TransactionsService} from './TransactionsService';

export interface ExtendedProfitTransaction extends Transaction {
    activity: string;
    assetProfit: string;
    amountProfit: number | string;
    amountEurProfit: number | string;
    costOfActivityProfit: number;
    costOfActivityEurProfit: number | string;
    sourceOfCostProfit: string;
    txFeeProfit: number;
    systemFeeProfit: number;
    profitMarginProfit: number;
    profitMarginEurProfit: number | string;
    totalAmountProfit: number;
    totalAmountEurProfit: number | string;
    commisionAccountProfit: string;
}

interface ProfitMarginServicePayment {
    id: number;
    action: number;
    type: string;
    profileType: string;
    disabled: boolean;
    value: number;
    key: string;
    note: string;
    referral: number;
    status: string;
}

interface ProfitMarginAccountAndBalanceMaintenanceFee {
    profile: string;
    code: string;
    paymentTypes: any[];
}

interface ProfitMarginAccountAndBalancePaymentType {
    isFixed: boolean;
    riskGroupPaymentId: number;
    riskGroupCode: string;
    type: string;
    subType: string;
    amount: string;
    minCommission: boolean;
    minCommissionAmount: string;
}

interface ProfitMarginCostOfProducts {
    key: string;
    name: string;
    feePct: number;
    feeEur: number;
    minEur: number;
    maxEur: number;
}

@Injectable()
export class ProfitMarginService {
    private servicePayments: ProfitMarginServicePayment[];
    private accountAndBalanceMaintenanceFee: Array<PaymentGroupFees>;
    private costOfProducts: ProfitMarginCostOfProducts[];

    constructor(
        private readonly commissionService: CommissionService,
        private readonly costOfProductService: CostOfProductService,
        private readonly transactionsService: TransactionsService
    ) { }

    public async loadServiceManagementSettings(type: string): Promise<ProfitMarginService> {
        this.servicePayments = await this.commissionService.getProductList(type).toPromise();
        this.accountAndBalanceMaintenanceFee = (await this.commissionService.getRiskGroupPayments(type).toPromise()).paymentFees;
        this.costOfProducts = await this.costOfProductService.loadData('PRIVATE').toPromise();
        return this;
    }

    public loadData(
        filter: string = '',
        orderBy: string = '',
        orderDirection: string = 'ASC',
        pageNumber: number = 0,
        pageSize: number = 10,
        globalFilter: string = null
    ): Observable<DataSourceResponse<Transaction>> {
        return this.transactionsService
            .loadData(filter, orderBy, orderDirection, pageNumber, pageSize, globalFilter)
            .pipe(
                map((dsr: DataSourceResponse<Transaction>) => {
                    const txs = dsr.list;
                    txs.forEach((tx) => {
                        this.calculateProfit(tx);
                    });
                    return dsr;
                })
            );
    }

    private calculateProfit(tx: Transaction): ExtendedProfitTransaction {
        try {
            switch (tx.type) {
                case 'EXCHANGE':
                    this.calculateProfitExchange(tx as ExtendedProfitTransaction);
                    break;
                case 'CRYPTO':
                    this.calculateProfitCrypto(tx as ExtendedProfitTransaction);
                    break;
                case 'SERVICE':
                    this.calculateProfitService(tx as ExtendedProfitTransaction);
                    break;
                // case 'CLEARJUNCTION':
                //     this.calculateProfitBank(tx as ExtendedProfitTransaction);
                //     break;
                case 'INTERCASH':
                    this.calculateProfitCard(tx as ExtendedProfitTransaction);
                    break;
                case 'CUSTODY':
                    this.calculateProfitTrust(tx as ExtendedProfitTransaction);
                    break;
            }
        } catch (e) {
            console.error(e);
        }
        return tx as ExtendedProfitTransaction;
    }

    //
    // TODO: Mock data here, make request for the rates
    //

    private calculateProfitCrypto(tx: ExtendedProfitTransaction): ExtendedProfitTransaction {
        tx.assetProfit = `${tx.currencyCodeFrom}`;
        const amountProfit = Number(
            tx.subType === 'DEPOSIT' ? tx.amountTo : tx.amountFrom
        );
        tx.amountProfit = amountProfit;

        const costOfActivity = 0;
        tx.costOfActivityProfit = costOfActivity;

        tx.sourceOfCostProfit = `Internal`;

        const txFee = Number(tx.txFee);
        tx.txFeeProfit = txFee;

        const systemFee = Number(tx.systemFee);
        tx.systemFeeProfit = systemFee;

        const profitMargin = Number(systemFee - costOfActivity);
        tx.profitMarginProfit = profitMargin;

        const totalAmount = Number(amountProfit + txFee + systemFee);
        tx.totalAmountProfit = totalAmount;

        tx.commisionAccountProfit = `${tx.currencyCodeFrom} Wallet`;

        ////// Eurs
        try {
            tx.amountEurProfit = Number(
                tx.subType === 'DEPOSIT' ? tx.amountToEur : tx.amountFromEur
            );
            tx.costOfActivityEurProfit = costOfActivity;
            tx.profitMarginEurProfit = 0;
            tx.totalAmountEurProfit = Number(tx.totalEur);
        } catch (e) {
            console.error(e);
        }
        /////

        return tx;
    }

    private calculateProfitExchange(tx: ExtendedProfitTransaction): ExtendedProfitTransaction {
        tx.assetProfit = `${tx.currencyCodeFrom} → ${tx.currencyCodeTo}`;

        const amountProfitFrom = Number(tx.amountFrom);
        const amountProfitTo = Number(tx.amountTo);
        tx.amountProfit = `${amountProfitFrom} → ${amountProfitTo}`;

        const krakenFeePct = 0.26;
        const costOfActivity = this.pctOfNumber(amountProfitFrom, krakenFeePct);
        tx.costOfActivityProfit = costOfActivity;

        tx.sourceOfCostProfit = `Kraken`;

        const systemFee = Number(tx.systemFee);
        tx.systemFeeProfit = systemFee;

        const profitMargin = Number(systemFee - costOfActivity);
        tx.profitMarginProfit = profitMargin;

        const totalAmount = Number(Number(amountProfitFrom) + systemFee);
        tx.totalAmountProfit = totalAmount;

        tx.commisionAccountProfit = `${tx.currencyCodeFrom} Wallet`;

        ////// Eurs
        try {
            tx.amountEurProfit = `${tx.amountFromEur} → ${tx.amountToEur}`;
            tx.costOfActivityEurProfit = this.pctOfNumber(tx.amountFromEur, krakenFeePct).toFixed(2);
            tx.profitMarginEurProfit = 0;
            tx.totalAmountEurProfit = Number(tx.totalEur).toFixed(2);
        } catch (e) {
            console.error(e);
        }
        /////

        return tx;
    }

    private calculateProfitService(tx: ExtendedProfitTransaction): ExtendedProfitTransaction {
        // KYC or not
        console.dir(tx);
        if (tx.productId === null || tx.productId === undefined) {
            // Other service payment
            console.warn(`ProfitMarginService.calculateProfitService Wrong productId == ${tx.productId}`);
            // TODO: defaulting to Product ID 1 if not found
            tx.productId = this.servicePayments[0].id;
        }

        const matchedProduct = this.servicePayments.find((p) => p.id === tx.productId);
        if (matchedProduct) {
            // KYC
            const txType = matchedProduct.note.replace(/Payment for/g, '').replace(/ level/g, '');
            if (!isTransactionType(txType)) {
              throw Error('Failed to extract transaction type from string.');
            }
            tx.type = txType;

            const assetCtypto = 'CRYPTO';
            const assetFiat = 'FIAT';
            tx.assetProfit = `${tx.currencyCodeFrom || assetCtypto || assetFiat}`;

            const amountProfit = Number(tx.amountFrom);
            tx.amountProfit = amountProfit;

            const costOfActivity = this.costOfProductFeeKYC(amountProfit, tx.subType);
            tx.costOfActivityProfit = costOfActivity;

            tx.sourceOfCostProfit = `Internal`;

            const txFee = Number(tx.txFee);
            tx.txFeeProfit = txFee;

            const systemFee = matchedProduct.disabled ? 0 : Number(matchedProduct.value);
            tx.systemFeeProfit = systemFee;

            const profitMargin = Number(systemFee - costOfActivity);
            tx.profitMarginProfit = profitMargin;

            const totalAmount = amountProfit + systemFee;
            tx.totalAmountProfit = totalAmount;

            tx.commisionAccountProfit = `${assetCtypto || assetFiat}`;

            ////// Eurs
            try {
                tx.amountEurProfit = Number(tx.amountFromEur);
                tx.costOfActivityEurProfit = this.costOfProductFeeKYC(amountProfit, tx.subType);
                tx.profitMarginEurProfit = Number(systemFee - tx.costOfActivityEurProfit);
                tx.totalAmountEurProfit = Number(tx.totalEur);
            } catch (e) {
                console.error(e);
            }
            /////
        } else {
            // Other service payment
            console.warn(
                `ProfitMarginService.calculateProfitService Product not matched with productId: ${tx.productId}`
            );
        }

        return tx;
    }

    // private calculateProfitBank(tx: ExtendedProfitTransaction): ExtendedProfitTransaction {
    //     tx.assetProfit = `${tx.currencyCodeFrom}`;
    //
    //     const amountProfit = Number(
    //         tx.subType === 'DEPOSIT' ? tx.amountTo : tx.amountFrom
    //     );
    //     tx.amountProfit = amountProfit;
    //
    //     const costOfActivity = this.costOfProductFeeBank(amountProfit, tx.subType);
    //     tx.costOfActivityProfit = costOfActivity;
    //
    //     tx.sourceOfCostProfit = `ClearJunction`;
    //
    //     const txFee = Number(tx.txFee);
    //     tx.txFeeProfit = txFee;
    //
    //     const systemFee = this.systemFeeForBank(amountProfit, tx.subType);
    //     tx.systemFeeProfit = systemFee;
    //
    //     const profitMargin = Number(systemFee - costOfActivity);
    //     tx.profitMarginProfit = profitMargin;
    //
    //     const totalAmount = Number(amountProfit + systemFee);
    //     tx.totalAmountProfit = totalAmount;
    //
    //     tx.commisionAccountProfit = `FIAT`;
    //
    //     ////// Eurs
    //     try {
    //         tx.amountEurProfit = amountProfit;
    //         tx.costOfActivityEurProfit = this.costOfProductFeeKYC(amountProfit, tx.subType);
    //         tx.profitMarginEurProfit = profitMargin;
    //         tx.totalAmountEurProfit = totalAmount;
    //     } catch (e) {
    //         console.error(e);
    //     }
    //     /////
    //
    //     return tx;
    // }

    private calculateProfitCard(tx: ExtendedProfitTransaction): ExtendedProfitTransaction {
        tx.assetProfit = `${tx.currencyCodeFrom}`;

        const amountProfit = Number(
            tx.subType === 'DEPOSIT' ? tx.amountTo : tx.amountFrom
        );
        tx.amountProfit = amountProfit;

        const costOfActivity = this.costOfProductFeeCard(amountProfit, tx.subType);
        tx.costOfActivityProfit = costOfActivity;

        tx.sourceOfCostProfit = `Intercash`;

        const txFee = Number(tx.txFee);
        tx.txFeeProfit = txFee;

        const systemFee = this.systemFeeForCard(amountProfit, tx.subType);
        tx.systemFeeProfit = systemFee;

        const profitMargin = Number(systemFee - costOfActivity);
        tx.profitMarginProfit = profitMargin;

        const totalAmount = Number(amountProfit + systemFee);
        tx.totalAmountProfit = totalAmount;

        tx.commisionAccountProfit = `FIAT`;

        ////// Eurs
        try {
            tx.amountEurProfit = amountProfit;
            tx.costOfActivityEurProfit = this.costOfProductFeeKYC(amountProfit, tx.subType);
            tx.profitMarginEurProfit = profitMargin;
            tx.totalAmountEurProfit = totalAmount;
        } catch (e) {
            console.error(e);
        }
        /////

        return tx;
    }

    private calculateProfitTrust(tx: ExtendedProfitTransaction): ExtendedProfitTransaction {
        tx.assetProfit = `${tx.currencyCodeFrom}`;

        const amountProfit = Number(
            tx.subType === 'DEPOSIT' ? tx.amountTo : tx.amountFrom
        );
        tx.amountProfit = amountProfit;

        const costOfActivity = this.costOfProductFeeTrust(amountProfit, tx.subType);
        tx.costOfActivityProfit = costOfActivity;

        tx.sourceOfCostProfit = `Trustcom`;

        const txFee = Number(tx.txFee);
        tx.txFeeProfit = txFee;

        const systemFee = this.systemFeeForTrust(amountProfit, tx.subType);
        tx.systemFeeProfit = systemFee;

        const profitMargin = Number(systemFee - costOfActivity);
        tx.profitMarginProfit = profitMargin;

        const totalAmount = Number(amountProfit + systemFee);
        tx.totalAmountProfit = totalAmount;

        tx.commisionAccountProfit = `${tx.currencyCodeFrom} Wallet`;

        ////// Eurs
        try {
            tx.amountEurProfit = amountProfit;
            tx.costOfActivityEurProfit = this.costOfProductFeeKYC(amountProfit, tx.subType);
            tx.profitMarginEurProfit = profitMargin;
            tx.totalAmountEurProfit = totalAmount;
        } catch (e) {
            console.error(e);
        }
        /////

        return tx;
    }

    private pctOfNumber(num: number, pct: number): number {
        const pctOfNum = num * (pct / 100);
        return pctOfNum;
    }

    private costOfProductFeeBank(num: number, subType: string): number {
        let costOfProduct: ProfitMarginCostOfProducts = null;
        if (subType === 'DEPOSIT') {
            costOfProduct = this.costOfProducts.find((c) => c.key === 'ClearJunctionDeposit');
        } else {
            costOfProduct = this.costOfProducts.find((c) => c.key === 'ClearJunctionWitdrawal');
        }

        const feePct = Number(costOfProduct.feePct) || 0;
        const feeEur = Number(costOfProduct.feeEur) || 0;
        const minEur = Number(costOfProduct.minEur) || 0;
        const maxEur = Number(costOfProduct.maxEur) || 0;

        const feePctFeeEur = (feePct > 0 ? this.pctOfNumber(num, feePct) : 0) + feeEur;
        const minFeePctFeeEur = minEur > 0 ? Math.max(feePctFeeEur, minEur) : feePctFeeEur;
        const maxMinFeePctFeeEur = maxEur > 0 ? Math.min(minFeePctFeeEur, maxEur) : minFeePctFeeEur;

        return maxMinFeePctFeeEur;
    }

    private costOfProductFeeCard(num: number, subType: string): number {
        let costOfProduct: ProfitMarginCostOfProducts = null;
        if (subType === 'DEPOSIT') {
            costOfProduct = this.costOfProducts.find((c) => c.key === 'CardTopUp');
        } else {
            costOfProduct = this.costOfProducts.find((c) => c.key === 'CardATMWithdrawalDomestic');
        }

        const feePct = Number(costOfProduct.feePct) || 0;
        const feeEur = Number(costOfProduct.feeEur) || 0;
        const minEur = Number(costOfProduct.minEur) || 0;
        const maxEur = Number(costOfProduct.maxEur) || 0;

        const feePctFeeEur = (feePct > 0 ? this.pctOfNumber(num, feePct) : 0) + feeEur;
        const minFeePctFeeEur = minEur > 0 ? Math.max(feePctFeeEur, minEur) : feePctFeeEur;
        const maxMinFeePctFeeEur = maxEur > 0 ? Math.min(minFeePctFeeEur, maxEur) : minFeePctFeeEur;

        return maxMinFeePctFeeEur;
    }


    private costOfProductFeeTrust(num: number, subType: string): number {
        let costOfProduct: ProfitMarginCostOfProducts = null;
        if (subType === 'DEPOSIT') {
            costOfProduct = this.costOfProducts.find((c) => c.key === 'TrustDeposit');
        } else {
            costOfProduct = this.costOfProducts.find((c) => c.key === 'TrustWitdrawal');
        }

        const feePct = Number(costOfProduct.feePct) || 0;
        const feeEur = Number(costOfProduct.feeEur) || 0;
        const minEur = Number(costOfProduct.minEur) || 0;
        const maxEur = Number(costOfProduct.maxEur) || 0;

        const feePctFeeEur = (feePct > 0 ? this.pctOfNumber(num, feePct) : 0) + feeEur;
        const minFeePctFeeEur = minEur > 0 ? Math.max(feePctFeeEur, minEur) : feePctFeeEur;
        const maxMinFeePctFeeEur = maxEur > 0 ? Math.min(minFeePctFeeEur, maxEur) : minFeePctFeeEur;

        return maxMinFeePctFeeEur;
    }

    private costOfProductFeeKYC(num: number, subType: string): number {
        let costOfProduct: ProfitMarginCostOfProducts = null;
        if (subType === 'DEPOSIT') {
            costOfProduct = this.costOfProducts.find((c) => c.key === 'SumSub');
        } else {
            costOfProduct = this.costOfProducts.find((c) => c.key === 'SumSub');
        }

        const feePct = Number(costOfProduct.feePct) || 0;
        const feeEur = Number(costOfProduct.feeEur) || 0;
        const minEur = Number(costOfProduct.minEur) || 0;
        const maxEur = Number(costOfProduct.maxEur) || 0;

        const feePctFeeEur = (feePct > 0 ? this.pctOfNumber(num, feePct) : 0) + feeEur;
        const minFeePctFeeEur = minEur > 0 ? Math.max(feePctFeeEur, minEur) : feePctFeeEur;
        const maxMinFeePctFeeEur = maxEur > 0 ? Math.min(minFeePctFeeEur, maxEur) : minFeePctFeeEur;

        return maxMinFeePctFeeEur;
    }

    // private systemFeeForBank(num: number, subType: string): number {
    //     let accountAndBalanceMaintenanceFee: PaymentGroupFees | null = null;
    //     if (subType === 'DEPOSIT') {
    //         accountAndBalanceMaintenanceFee = this.accountAndBalanceMaintenanceFee.find((c) => c.code === 'INCOMING_BANK_TRANSFER');
    //     } else {
    //         accountAndBalanceMaintenanceFee = this.accountAndBalanceMaintenanceFee.find((c) => c.code === 'OUTGOING_BANK_TRANSFER');
    //     }
    //
    //     // TODO: okertanov: We have no direct linking between TX and User Risk Group.
    //     const maintenanceFee = accountAndBalanceMaintenanceFee.paymentTypes['LOW'] as ProfitMarginAccountAndBalancePaymentType;
    //
    //     // TODO: okertanov: To document API part for 'risk_group_payment_type'.
    //     const minCommissionEur = maintenanceFee.isFixed ? Number(maintenanceFee.amount) : 0;
    //     const minCommissionPct = !maintenanceFee.isFixed ? Number(maintenanceFee.amount) : 0;
    //     const minCommissionAmountEur = maintenanceFee.minCommission ? Number(maintenanceFee.minCommissionAmount) : 0;
    //
    //     const minCommission = minCommissionEur + (minCommissionPct > 0 ? this.pctOfNumber(num, minCommissionPct) : 0);
    //     const minCommissionMin = minCommissionAmountEur > 0 ? Math.max(minCommission, minCommissionAmountEur) : minCommission;
    //
    //     return minCommissionMin;
    // }

    private systemFeeForCard(num: number, subType: string): number {
        let accountAndBalanceMaintenanceFee: ProfitMarginAccountAndBalanceMaintenanceFee = null;
        if (subType === 'DEPOSIT') {
            accountAndBalanceMaintenanceFee = this.accountAndBalanceMaintenanceFee.find((c) => c.code === 'CARD_TOP_UP_TRANSFER') as any;
        } else {
            accountAndBalanceMaintenanceFee = this.accountAndBalanceMaintenanceFee.find((c) => c.code === 'OUTGOING_WIRE_TRANSFER') as any;
        }

        // TODO: okertanov: We have no direct linking between TX and User Risk Group.
        const maintenanceFee = accountAndBalanceMaintenanceFee.paymentTypes['LOW'] as ProfitMarginAccountAndBalancePaymentType;

        // TODO: okertanov: To document API part for 'risk_group_payment_type'.
        const minCommissionEur = maintenanceFee.isFixed ? Number(maintenanceFee.amount) : 0;
        const minCommissionPct = !maintenanceFee.isFixed ? Number(maintenanceFee.amount) : 0;
        const minCommissionAmountEur = maintenanceFee.minCommission ? Number(maintenanceFee.minCommissionAmount) : 0;

        const minCommission = minCommissionEur + (minCommissionPct > 0 ? this.pctOfNumber(num, minCommissionPct) : 0);
        const minCommissionMin = minCommissionAmountEur > 0 ? Math.max(minCommission, minCommissionAmountEur) : minCommission;

        return minCommissionMin;
    }

    private systemFeeForTrust(num: number, subType: string): number {
        let accountAndBalanceMaintenanceFee: ProfitMarginAccountAndBalanceMaintenanceFee = null;
        if (subType === 'DEPOSIT') {
            accountAndBalanceMaintenanceFee = this.accountAndBalanceMaintenanceFee.find((c) => c.code === 'INCOMING_WIRE_TRANSFER_EUR_TRUST') as any;
        } else {
            accountAndBalanceMaintenanceFee = this.accountAndBalanceMaintenanceFee.find((c) => c.code === 'OUTGOING_WIRE_TRANSFER_EUR_TRUST') as any;
        }

        // TODO: okertanov: We have no direct linking between TX and User Risk Group.
        const maintenanceFee = accountAndBalanceMaintenanceFee.paymentTypes['LOW'] as ProfitMarginAccountAndBalancePaymentType;

        // TODO: okertanov: To document API part for 'risk_group_payment_type'.
        const minCommissionEur = maintenanceFee.isFixed ? Number(maintenanceFee.amount) : 0;
        const minCommissionPct = !maintenanceFee.isFixed ? Number(maintenanceFee.amount) : 0;
        const minCommissionAmountEur = maintenanceFee.minCommission ? Number(maintenanceFee.minCommissionAmount) : 0;

        const minCommission = minCommissionEur + (minCommissionPct > 0 ? this.pctOfNumber(num, minCommissionPct) : 0);
        const minCommissionMin = minCommissionAmountEur > 0 ? Math.max(minCommission, minCommissionAmountEur) : minCommission;

        return minCommissionMin;
    }
}
